import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, ListModel, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { CreateTaskRequest, initialTaskValue, Task, TasksSliceModel, UpdateTaskRequest } from "./tasks-interface";
import { Activity } from "./activities-interface";
import { API_BASE_URL as baseUri } from "config/env";

export const GetTaskById = createAsyncThunk<Task, Guid, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetTaskById',
    async (taskId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/get?taskId=${taskId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Task;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTasksByContact = createAsyncThunk<Task[], Guid, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetTasksByContact',
    async (contactId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/get-by-contact?contactId=${contactId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Task[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTasksByCompany = createAsyncThunk<Task[], Guid, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetTasksByCompany',
    async (companyId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/get-by-company?companyId=${companyId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Task[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTasksByEmployee = createAsyncThunk<Task[], void, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetTasksByEmployee',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/get-by-employee`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Task[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetTasksByCreator = createAsyncThunk<Task[], void, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetTasksByCreator',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/get-by-creator`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Task[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateTask = createAsyncThunk<Activity, CreateTaskRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/CreateTaskEndpoint',
    async (createTaskRequest: CreateTaskRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/create`, {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(createTaskRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateTask = createAsyncThunk<Activity, UpdateTaskRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/UpdateTaskEndpoint',
    async (createTaskRequest: UpdateTaskRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/tasks/update`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(createTaskRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/TasksSlice/GetContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: TasksSliceModel = {
    task: undefined,
    taskModel: initialTaskValue,
    tasksByContact: [],
    tasksByCompany: [],
    tasksByEmployee: [],
    tasksByCreator: [],

    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,

    isLoading: false,

    filteredPartnersList: FilteredDropDownModelInit,
    filteredContactsList: FilteredDropDownModelInit
}

export const TasksSlice = createSlice({
    name: 'TasksSlice',
    initialState: initialValue,
    reducers: {
        resetTasksSlice: (state) => {
            state = initialValue
        },
        resetTaskStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetTaskModelAndLists: (state) => {
            state.taskModel = initialTaskValue;
            state.filteredContactsList = FilteredDropDownModelInit;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        setSelectedPartners: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.taskModel) {
                state.taskModel.associatedCompanies = action.payload
            }
        },
        setSelectedContacts: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.taskModel) {
                state.taskModel.associatedContacts = action.payload
            }
        },
    },
    extraReducers(builder) {
        builder.addCase(GetTaskById.fulfilled, (state, action: PayloadAction<Task>) => {
            state.isLoading = false;
            state.task = action.payload;
            state.taskModel.id = action.payload.id;
            state.taskModel.name = action.payload.name;
            state.taskModel.associatedCompanies = action.payload.companies;
            state.taskModel.associatedContacts = action.payload.contacts;
            state.taskModel.taskDate = action.payload.activityDate!;
            state.taskModel.priorityId = action.payload.priorityId;
            state.taskModel.typeId = action.payload.taskTypeId;
            state.taskModel.note = action.payload.taskNote;
            state.taskModel.assignedToId = action.payload.assignedToId;
            state.taskModel.reminder = action.payload.reminder;
            state.taskModel.taskStatusId = action.payload.taskStatusId;
        })
            .addCase(GetTaskById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTaskById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetTasksByContact.fulfilled, (state, action: PayloadAction<Task[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.tasksByContact = action.payload
        })
            .addCase(GetTasksByContact.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTasksByContact.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetTasksByCompany.fulfilled, (state, action: PayloadAction<Task[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.tasksByCompany = action.payload;
        })
            .addCase(GetTasksByCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTasksByCompany.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetTasksByEmployee.fulfilled, (state, action: PayloadAction<Task[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.tasksByEmployee = action.payload;
        })
            .addCase(GetTasksByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTasksByEmployee.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetTasksByCreator.fulfilled, (state, action: PayloadAction<Task[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.tasksByCreator = action.payload;
        })
            .addCase(GetTasksByCreator.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetTasksByCreator.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(CreateTask.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.message = "The task is successfully created."
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(CreateTask.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateTask.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UpdateTask.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.message = "The task is successfully updated."
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(UpdateTask.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateTask.pending, state => {
                state.isLoading = true;
            })
        //Get Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
        //Get contacts list
        builder.addCase(GetContactsForDropdown.pending, (state) => {
            state.filteredContactsList.loading = true;
            state.filteredContactsList.error = null;
        })
            .addCase(GetContactsForDropdown.fulfilled, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.data = action.payload.data;
                state.filteredContactsList.totalItems = action.payload.totalItems;
                state.filteredContactsList.totalPages = action.payload.totalPages;
                state.filteredContactsList.currentPage = action.payload.currentPage;
            })
            .addCase(GetContactsForDropdown.rejected, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.error = action.payload?.message || 'Error fetching data';
            });

    }
});

export const { resetTasksSlice, resetTaskStatusCode, setSelectedPartners, setSelectedContacts, resetTaskModelAndLists } = TasksSlice.actions;
export default TasksSlice.reducer;